import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useCreateProjectMutation,
    useGetPersonMutation,
    useUpdateProjectMutation,
    useUpdatePersonStatusMutation,
    useUpdateOutgoingMutation,
    useDeletePersonMutation,
    useSendAccessLinkMutation
} from 'services/person/person.endpoint';
import { useApiToast } from 'components/Toaster';
import { CreateProjectPayload, Person, UpdateProjectPayload, UpdatePersonStatusPayload } from 'services/person/person.types';
import { useSelector } from 'react-redux';
import { RootState } from 'features/store';
import { getUser } from 'features/auth/auth.slice';

export default function usePersonDetailsPageVM() {
    const [person, setPerson] = useState<Person | null>(null);
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [projectModalMode, setProjectModalMode] = useState<'create' | 'update'>('create');
    const [isOutgoingModalOpen, setIsOutgoingModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [outgoingModalMode, setOutgoingModalMode] = useState<'outgoing' | 'entering'>('outgoing');
    let { personId } = useParams();
    const { showApiToast, showErrorToast } = useApiToast();
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => getUser(state));

    const [
        getPersonDetails,
        {
            isLoading: isGetPersonDetailsLoading,
            isError: isGetPersonDetailsError,
            isSuccess: isGetPersonDetailsSuccess,
            error: personDetailsError,
            data: personDetailsResponse
        }
    ] = useGetPersonMutation();

    const [
        deletePerson,
        {
            isLoading: isDeletePersonLoading,
            isError: isDeletePersonError,
            isSuccess: isDeletePersonSuccess,
            error: deletePersonError,
            data: deletePersonResponse
        }
    ] = useDeletePersonMutation();

    const [
        createProject,
        {
            isLoading: isCreateProjectLoading,
            isError: isCreateProjectError,
            isSuccess: isCreateProjectSuccess,
            error: createProjectError,
            data: createProjectResponse
        }
    ] = useCreateProjectMutation();
    const [
        updateProject,
        {
            isLoading: isUpdateProjectLoading,
            isError: isUpdateProjectError,
            isSuccess: isUpdateProjectSuccess,
            error: updateProjectError,
            data: updateProjectResponse
        }
    ] = useUpdateProjectMutation();
    const [
        updatePersonStatus,
        {
            isLoading: isUpdatePersonStatusLoading,
            isError: isUpdatePersonStatusError,
            isSuccess: isUpdatePersonStatusSuccess,
            error: updatePersonStatusError,
            data: updatePersonStatusResponse
        }
    ] = useUpdatePersonStatusMutation();

    const [
        updateOutgoing,
        {
            isLoading: isUpdateOutgoingLoading,
            isError: isUpdateOutgoingError,
            isSuccess: isUpdateOutgoingSuccess,
            error: updateOutgoingError,
            data: updateOutgoingResponse
        }
    ] = useUpdateOutgoingMutation();

    const [
        sendAccessLink,
        {
            isLoading: isSendAccessLinkLoading,
            isError: isSendAccessLinkError,
            isSuccess: isSendAccessLinkSuccess,
            error: sendAccessLinkError,
            data: sendAccessLinkResponse
        }
    ] = useSendAccessLinkMutation();

    const handleCreateProject = (values: CreateProjectPayload) => {
        createProject({
            id: person?.id || 0,
            label: values.label,
            initial_request: values.initial_request
        });
        setIsProjectModalOpen(false);
    };

    const handleUpdateProject = (values: UpdateProjectPayload) => {
        updateProject({ id: person?.id || 0, project_id: values.project_id, label: values.label, initial_request: values.initial_request });
        setProjectModalMode('create');
        setIsProjectModalOpen(false);
    };

    const handleUpdateOutgoing = (reason: string, outgoing: boolean) => {
        updateOutgoing({ id: person?.id || 0, body: { reason, outgoing } });
        setIsOutgoingModalOpen(false);
    };

    const handleUpdatePersonStatus = (status: string) => {
        updatePersonStatus({ id: person?.id || 0, status });
    };

    const handleDeletePerson = (id: number) => {
        deletePerson({ id });
    };

    const handleSendAccessLink = (id: number) => {
        sendAccessLink({ id });
    };

    useEffect(() => {
        if (isDeletePersonLoading) return;
        if (isDeletePersonError && deletePersonError) {
            showErrorToast(deletePersonError, {
                message: 'Erreur de suppression de la personne'
            });
            return;
        }
        if (isDeletePersonSuccess && deletePersonResponse) {
            showApiToast(deletePersonResponse, {
                successMessage: 'Personne supprimée avec succès',
                errorMessage: 'Erreur de suppression de la personne'
            });
            navigate('/app/admin/persons');
            return;
        }
    }, [isDeletePersonLoading, isDeletePersonError, deletePersonError, isDeletePersonSuccess, deletePersonResponse]);

    useEffect(() => {
        if (personId) {
            getPersonDetails({ id: parseInt(personId) });
        }
    }, [personId, getPersonDetails]);

    useEffect(() => {
        if (isGetPersonDetailsLoading) return;
        if (isGetPersonDetailsError && personDetailsError) {
            showErrorToast(personDetailsError, {
                message: 'Erreur de chargement des informations de la personne'
            });
            return;
        }
        if (isGetPersonDetailsSuccess && personDetailsResponse) {
            setPerson(personDetailsResponse.data);
            return;
        }
    }, [
        isGetPersonDetailsLoading,
        isGetPersonDetailsError,
        isGetPersonDetailsSuccess,
        personDetailsError,
        personDetailsResponse,
        setPerson,
        showErrorToast
    ]);

    useEffect(() => {
        if (isCreateProjectLoading) return;
        if (isCreateProjectError && createProjectError) {
            showErrorToast(createProjectError, {
                message: 'Erreur de création du projet'
            });
            return;
        }
        if (isCreateProjectSuccess && createProjectResponse) {
            showApiToast(createProjectResponse, {
                successMessage: 'Projet créé avec succès',
                errorMessage: 'Erreur de création du projet'
            });
            //Bug ? refresh all the content of the page
            getPersonDetails({ id: parseInt(personId || '0') });
            return;
        }
    }, [isCreateProjectLoading, isCreateProjectError, createProjectError, isCreateProjectSuccess, createProjectResponse]);

    useEffect(() => {
        if (isUpdateProjectLoading) return;
        if (isUpdateProjectError && updateProjectError) {
            showErrorToast(updateProjectError, {
                message: 'Erreur de mise à jour du projet'
            });
            return;
        }
        if (isUpdateProjectSuccess && updateProjectResponse) {
            showApiToast(updateProjectResponse, {
                successMessage: 'Projet mis à jour avec succès',
                errorMessage: 'Erreur de mise à jour du projet'
            });
            getPersonDetails({ id: parseInt(personId || '0') });
            return;
        }
    }, [isUpdateProjectLoading, isUpdateProjectError, updateProjectError, showErrorToast]);

    useEffect(() => {
        if (isUpdatePersonStatusLoading) return;
        if (isUpdatePersonStatusError && updatePersonStatusError) {
            showErrorToast(updatePersonStatusError, {
                message: 'Erreur de mise à jour du statut'
            });
            return;
        }
        if (isUpdatePersonStatusSuccess && updatePersonStatusResponse) {
            showApiToast(updatePersonStatusResponse, {
                successMessage: 'Statut mis à jour avec succès',
                errorMessage: 'Erreur de mise à jour du statut'
            });
            getPersonDetails({ id: parseInt(personId || '0') });
            return;
        }
    }, [isUpdatePersonStatusLoading, isUpdatePersonStatusError, updatePersonStatusError, showErrorToast]);

    useEffect(() => {
        if (isUpdateOutgoingLoading) return;
        if (isUpdateOutgoingError && updateOutgoingError) {
            showErrorToast(updateOutgoingError, {
                message: "Erreur de mise à jour de l'état de sortie"
            });
            return;
        }
        if (isUpdateOutgoingSuccess && updateOutgoingResponse) {
            showApiToast(updateOutgoingResponse, {
                successMessage: 'État de sortie mis à jour avec succès',
                errorMessage: "Erreur de mise à jour de l'état de sortie"
            });
            getPersonDetails({ id: parseInt(personId || '0') });
            return;
        }
    }, [isUpdateOutgoingLoading, isUpdateOutgoingError, updateOutgoingError, showErrorToast]);

    useEffect(() => {
        if (isSendAccessLinkLoading) return;
        if (isSendAccessLinkError && sendAccessLinkError) {
            showErrorToast(sendAccessLinkError, {
                message: "Erreur d'envoi du lien d'accès"
            });
        }
        if (isSendAccessLinkSuccess && sendAccessLinkResponse) {
            showApiToast(sendAccessLinkResponse, {
                successMessage: "Lien d'accès envoyé avec succès",
                errorMessage: "Erreur d'envoi du lien d'accès"
            });
        }
    }, [isSendAccessLinkLoading, isSendAccessLinkError, sendAccessLinkError, showErrorToast]);

    return {
        getPersonDetails,
        isGetPersonDetailsLoading,
        personDetailsError,
        person,
        setPerson,
        personId,
        isProjectModalOpen,
        setIsProjectModalOpen,
        projectModalMode,
        setProjectModalMode,
        handleCreateProject,
        handleUpdateProject,
        handleUpdatePersonStatus,
        isOutgoingModalOpen,
        setIsOutgoingModalOpen,
        outgoingModalMode,
        setOutgoingModalMode,
        handleUpdateOutgoing,
        isUpdateOutgoingLoading,
        setIsDeleteModalOpen,
        isDeleteModalOpen,
        handleDeletePerson,
        userRole: user?.role,
        handleSendAccessLink
    };
}
